import { PageHeader } from "@/components/layout/PageHeader";
import { KpiCard } from "@/components/shared/KpiCard";
import { Loading } from "@/components/shared/Loading";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAnalytics } from "@/api/hooks";
import {
  BarChart, Bar, LineChart, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const PALETTE = ["#4f46e5", "#10b981", "#3b82f6", "#f59e0b", "#8b5cf6"];

function ChartCard({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">{title}</div>
      <div className="h-64 w-full min-w-0">{children}</div>
    </div>
  );
}

const axis = { tick: { fontSize: 11, fill: "#94a3b8" }, axisLine: false, tickLine: false };
const tooltipStyle = { contentStyle: { borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 } };

export default function AnalyticsDashboard() {
  const { data: analytics } = useAnalytics();
  if (!analytics) return <Loading />;
  return (
    <>
      <PageHeader title="Analytics & Performance" subtitle="Live reporting across Sales, Operations, Finance and assisted workflows" />
      <div className="flex-1 overflow-y-auto p-6">
        <Tabs defaultValue="sales">
          <TabsList>
            <TabsTrigger value="sales" data-testid="an-sales">Sales</TabsTrigger>
            <TabsTrigger value="operations" data-testid="an-ops">Operations</TabsTrigger>
            <TabsTrigger value="finance" data-testid="an-finance">Finance</TabsTrigger>
            <TabsTrigger value="ai" data-testid="an-ai">AI Quality</TabsTrigger>
          </TabsList>

          {/* SALES */}
          <TabsContent value="sales" className="mt-5 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{analytics.sales.kpis.map((k) => <KpiCard key={k.label} {...k} />)}</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Conversion funnel">
                <ResponsiveContainer width="99%" height="100%" minHeight={240}><BarChart data={analytics.sales.conversion} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" {...axis} /><YAxis type="category" dataKey="stage" width={80} {...axis} /><Tooltip {...tooltipStyle} />
                  <Bar dataKey="value" fill="#4f46e5" radius={[0, 6, 6, 0]} barSize={22} />
                </BarChart></ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Lead sources">
                <ResponsiveContainer width="99%" height="100%" minHeight={240}><BarChart data={analytics.sales.sources} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" {...axis} /><YAxis type="category" dataKey="name" width={80} {...axis} /><Tooltip {...tooltipStyle} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={20}>
                    {analytics.sales.sources.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart></ResponsiveContainer>
              </ChartCard>
            </div>
          </TabsContent>

          {/* OPERATIONS */}
          <TabsContent value="operations" className="mt-5 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{analytics.operations.kpis.map((k) => <KpiCard key={k.label} {...k} />)}</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Active jobs by mode">
                <ResponsiveContainer width="99%" height="100%" minHeight={240}><BarChart data={analytics.operations.byMode}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="mode" {...axis} /><YAxis {...axis} /><Tooltip {...tooltipStyle} />
                  <Bar dataKey="value" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={30} />
                </BarChart></ResponsiveContainer>
              </ChartCard>
              <ChartCard title="On-time milestone %">
                <ResponsiveContainer width="99%" height="100%" minHeight={240}><BarChart data={analytics.operations.milestones}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="name" {...axis} /><YAxis domain={[0, 100]} {...axis} /><Tooltip {...tooltipStyle} />
                  <Bar dataKey="ontime" fill="#10b981" radius={[6, 6, 0, 0]} barSize={30} />
                </BarChart></ResponsiveContainer>
              </ChartCard>
            </div>
          </TabsContent>

          {/* FINANCE */}
          <TabsContent value="finance" className="mt-5 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{analytics.finance.kpis.map((k) => <KpiCard key={k.label} {...k} />)}</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Revenue & gross profit (AED '000)">
                <ResponsiveContainer width="99%" height="100%" minHeight={240}><LineChart data={analytics.finance.revenue}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="m" {...axis} /><YAxis {...axis} /><Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="rev" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="gp" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart></ResponsiveContainer>
              </ChartCard>
              <ChartCard title="AR aging (AED '000)">
                <ResponsiveContainer width="99%" height="100%" minHeight={240}><BarChart data={analytics.finance.aging}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="bucket" {...axis} /><YAxis {...axis} /><Tooltip {...tooltipStyle} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={34}>
                    {analytics.finance.aging.map((e, i) => <Cell key={i} fill={i >= 3 ? "#ef4444" : i === 2 ? "#f59e0b" : "#4f46e5"} />)}
                  </Bar>
                </BarChart></ResponsiveContainer>
              </ChartCard>
            </div>
          </TabsContent>

          {/* AI */}
          <TabsContent value="ai" className="mt-5 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{analytics.ai.kpis.map((k) => <KpiCard key={k.label} {...k} />)}</div>
            <ChartCard title="Assisted documents vs human overrides">
              <ResponsiveContainer width="99%" height="100%" minHeight={240}><BarChart data={analytics.ai.overrides}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="m" {...axis} /><YAxis {...axis} /><Tooltip {...tooltipStyle} />
                <Bar dataKey="auto" name="Assisted documents" fill="#8b5cf6" radius={[6, 6, 0, 0]} barSize={26} />
                <Bar dataKey="override" name="Human overrides" fill="#cbd5e1" radius={[6, 6, 0, 0]} barSize={26} />
              </BarChart></ResponsiveContainer>
            </ChartCard>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
